import React from "react";
import { useTranslationStore } from "../../../state/useTranslationStore";
import AddLanguageButton from "./AddLanguageButton";
import { toast } from "sonner";

// Map known extra info labels to shortcode tags
const EXTRA_INFO_SHORTCODES: Record<string, string> = {
  "Current Local Time": "[Fp_Single_CurrentTime]",
  "Current Date": "[Fp_Single_CurrentDate]",
  "Current Title": "[Fp_Single_Current_Title]",
  "Current Prayer": "[Fp_Single_Current_Title]",
  "The Current Prayer": "[Fp_Single_Current_Title]",
  "Current Location": "[Fp_Single_Current_Location]",
  "Masjid Name": "[Fp_Single_Masjid_Name]",
  "Next Prayer Title": "[Fp_Single_Next_Prayer_Title]",
  "Next Prayer Begins Title": "[Fp_Single_Next_Prayer_Begins_Title]",
  "Next Prayer Begins Time": "[Fp_Single_Next_Prayer_Begins_Time]",
  "Next Prayer Iqamah Title": "[Fp_Single_Next_Prayer_Iqamah_Title]",
  "Next Prayer Iqamah Time": "[Fp_Single_Next_Prayer_Iqamah_Time]",
  "Prayer Times For Today": "[Fp_Single_PTFT]",
};
// Single fixed badge class for consistency
const badgeClass =
  "ml-2 inline-block px-2 py-0.5 text-xs rounded border bg-green-100 text-green-800 border-green-200";

const ExtraInfo: React.FC = () => {
  const {
    extra_info,
    activeLanguage,
    availableLanguages,
    loading,
    selectLanguage,
    updateTranslation,
    saveTranslations,
    addNewLanguage,
  } = useTranslationStore();

  // Filter extra_info for the active language and track original index
  const activeExtraInfo = extra_info
    .map((item, idx) => ({ ...item, originalIndex: idx }))
    .filter((item) => item.language_code === activeLanguage);

  // Handle input changes using original index
  const handleInputChange = (originalIndex: number, value: string) => {
    if (originalIndex >= 0 && originalIndex < extra_info.length) {
      updateTranslation("extra_info", originalIndex, value);
    } else {
      console.error("Invalid index for extra_info");
    }
  };

  // Handle saving translations
  const handleSave = async () => {
    try {
      await saveTranslations();
      toast.success("Extra info translations updated successfully!");
    } catch (error) {
      console.error("Error saving extra info translations:", error);
      toast.error("Failed to save extra info translations.");
    }
  };

  return (
    <div className="extra-info-editor p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-green-600">
        Edit Extra Info Translations
      </h1>

      {/* Language Selection */}
      <div className="mb-4 flex gap-4 flex-wrap">
        <div className="space-x-3">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={`px-3 py-2 rounded-md focus:outline-none focus:ring-2 capitalize ${
                activeLanguage === lang.code
                  ? "bg-green-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {lang.language}
            </button>
          ))}
        </div>
        <AddLanguageButton
          data={extra_info}
          handlerAddLangauge={addNewLanguage}
          disabled={loading}
        />
      </div>

      {/* Translation Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeExtraInfo.map((item) => (
          <div
            key={`${item.original_text}-${item.language_code}`}
            className="flex flex-col gap-2"
          >
            <label className="text-gray-700 font-medium">
              {item.original_text} ({item.language_code})
              {EXTRA_INFO_SHORTCODES[item.original_text] && (
                <span className={badgeClass}>
                  {EXTRA_INFO_SHORTCODES[item.original_text]}
                </span>
              )}
            </label>
            <input
              type="text"
              value={item.translated_text}
              onChange={(e) => handleInputChange(item.originalIndex, e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default ExtraInfo;
